/**
 * Officer Slice
 * 
 * Manages officers state and actions
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import officerService from '../../services/officerService';

// Initial state
const initialState = {
  officers: [],
  currentOfficer: null,
  isLoading: false,
  error: null,
};

// Async thunks
export const fetchOfficers = createAsyncThunk(
  'officers/fetchAll',
  async ({ params = {}, role = 'cvo' }, { rejectWithValue }) => {
    try {
      const response = await officerService.getOfficers(params, role);
      if (response.success) {
        return response.data;
      }
      return rejectWithValue(response.message);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchOfficer = createAsyncThunk(
  'officers/fetchOne',
  async ({ id, role = 'cvo' }, { rejectWithValue }) => {
    try {
      const response = await officerService.getOfficer(id, role);
      if (response.success) {
        return response.data;
      }
      return rejectWithValue(response.message);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const createOfficer = createAsyncThunk(
  'officers/create',
  async ({ officerData, role = 'cvo' }, { rejectWithValue }) => {
    try {
      // Transform data to API payload format
      const payload = {
        employee_id: officerData.employmentId,
        name: officerData.name,
        designation_id: Number(officerData.designation),
        email: officerData.email,
        mobile: officerData.phone,
        department_id: Number(officerData.department),
        role_id: 3, // Default role for authority
        menu_ids: (officerData.menuIds || []).map(id => Number(id)),
      };

      const response = await officerService.createOfficer(payload, role);
      if (response.success) {
        // Extract user object from response (API returns { message, user, assigned_menus })
        return response.data.user || response.data;
      }
      return rejectWithValue(response.message);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateOfficer = createAsyncThunk(
  'officers/update',
  async ({ id, data, role = 'cvo' }, { rejectWithValue }) => {
    try {
      // Transform data to API payload format
      const payload = {
        employee_id: data.employmentId,
        name: data.name,
        designation_id: Number(data.designation),
        email: data.email,
        mobile: data.phone,
        department_id: Number(data.department),
        role_id: 3, // Default role for authority
        menu_ids: (data.menuIds || []).map(id => Number(id)),
      };

      // Add status if provided
      if (data.status) {
        payload.status = data.status;
      }

      const response = await officerService.updateOfficer(id, payload, role);
      if (response.success) {
        return response.data;
      }
      return rejectWithValue(response.message);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const deleteOfficer = createAsyncThunk(
  'officers/delete',
  async ({ id, role = 'cvo' }, { rejectWithValue }) => {
    try {
      const response = await officerService.deleteOfficer(id, role);
      if (response.success) {
        return id;
      }
      return rejectWithValue(response.message);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const activateOfficer = createAsyncThunk(
  'officers/activate',
  async ({ id, role = 'cvo' }, { rejectWithValue }) => {
    try {
      const response = await officerService.activateOfficer(id, role);
      if (response.success) {
        return response.data;
      }
      return rejectWithValue(response.message);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const deactivateOfficer = createAsyncThunk(
  'officers/deactivate',
  async ({ id, role = 'cvo' }, { rejectWithValue }) => {
    try {
      const response = await officerService.deactivateOfficer(id, role);
      if (response.success) {
        return response.data;
      }
      return rejectWithValue(response.message);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Officer slice
const officerSlice = createSlice({
  name: 'officers',
  initialState,
  reducers: {
    setCurrentOfficer: (state, action) => {
      state.currentOfficer = action.payload;
    },
    clearCurrentOfficer: (state) => {
      state.currentOfficer = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch officers
    builder
      .addCase(fetchOfficers.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchOfficers.fulfilled, (state, action) => {
        state.isLoading = false;
        // Handle different API response formats: { data: [...] } or direct array
        const officers = action.payload?.data || action.payload?.officers || action.payload;
        state.officers = Array.isArray(officers) ? officers : [];
      })
      .addCase(fetchOfficers.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });

    // Fetch single officer
    builder
      .addCase(fetchOfficer.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchOfficer.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentOfficer = action.payload;
      })
      .addCase(fetchOfficer.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });

    // Create officer
    builder
      .addCase(createOfficer.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createOfficer.fulfilled, (state, action) => {
        state.isLoading = false;
        state.officers.unshift(action.payload);
      })
      .addCase(createOfficer.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });

    // Update officer
    builder
      .addCase(updateOfficer.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateOfficer.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.officers.findIndex(o => o.id === action.payload.id);
        if (index !== -1) {
          state.officers[index] = action.payload;
        }
        if (state.currentOfficer?.id === action.payload.id) {
          state.currentOfficer = action.payload;
        }
      })
      .addCase(updateOfficer.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });

    // Delete officer
    builder
      .addCase(deleteOfficer.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteOfficer.fulfilled, (state, action) => {
        state.isLoading = false;
        state.officers = state.officers.filter(o => o.id !== action.payload);
      })
      .addCase(deleteOfficer.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });

    // Activate/Deactivate officer
    builder
      .addCase(activateOfficer.fulfilled, (state, action) => {
        const index = state.officers.findIndex(o => o.id === action.payload.id);
        if (index !== -1) {
          state.officers[index] = action.payload;
        }
      })
      .addCase(deactivateOfficer.fulfilled, (state, action) => {
        const index = state.officers.findIndex(o => o.id === action.payload.id);
        if (index !== -1) {
          state.officers[index] = action.payload;
        }
      });
  },
});

// Export actions
export const { setCurrentOfficer, clearCurrentOfficer, clearError } = officerSlice.actions;

// Export selectors
export const selectOfficers = (state) => state.officers.officers;
export const selectCurrentOfficer = (state) => state.officers.currentOfficer;
export const selectOfficerLoading = (state) => state.officers.isLoading;
export const selectOfficerError = (state) => state.officers.error;
export const selectActiveOfficers = (state) => 
  state.officers.officers.filter(o => o.status === 'Active');

// Export reducer
export default officerSlice.reducer;

